import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, X, Sparkles, Loader2 } from "lucide-react";
import { TOUR_MAP } from "./tourContent";

/** Post-login role-aware guided tour. Auto-opens once per role per user.
 *  Backing store: `GET /api/onboarding/status` returns pending tours;
 *  `POST /api/onboarding/complete` marks them done.
 *
 *  Replay: any element can navigate to `?tour=<role>` and this component
 *  will open the requested tour immediately (even if already completed).
 *
 *  Never shown to admin/employee roles. */
export default function OnboardingTour() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [params, setParams] = useSearchParams();
  const forcedRole = params.get("tour");   // e.g. "?tour=buyer"

  const [pending, setPending] = useState([]);      // ordered list of tour keys to show
  const [tourIdx, setTourIdx] = useState(0);       // which tour in the pending list
  const [step, setStep] = useState(0);             // slide within the current tour
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  // 1. Handle "?tour=<role>" — replay any tour on demand (but never for staff)
  useEffect(() => {
    if (!user || !forcedRole || !TOUR_MAP[forcedRole]) return;
    if (user.role === "admin" || user.role === "employee") {
      // Strip the param so a stray URL doesn't leave an admin in a stuck state
      const next = new URLSearchParams(params);
      next.delete("tour");
      setParams(next, { replace: true });
      return;
    }
    setPending([forcedRole]);
    setTourIdx(0);
    setStep(0);
    setChecked(true);
    // Strip the query param so a page refresh doesn't loop the tour
    const next = new URLSearchParams(params);
    next.delete("tour");
    setParams(next, { replace: true });
  }, [forcedRole, user]);

  // 2. Auto-launch pending tours on first login (skips admins)
  useEffect(() => {
    if (!user || checked || forcedRole) return;
    if (user.role === "admin" || user.role === "employee") {
      setChecked(true);
      return;
    }
    api
      .get("/onboarding/status")
      .then((r) => {
        setPending(r.data.pending || []);
        setChecked(true);
      })
      .catch(() => setChecked(true));
  }, [user, checked, forcedRole]);

  const tour = useMemo(() => (pending[tourIdx] ? TOUR_MAP[pending[tourIdx]] : null), [pending, tourIdx]);

  if (!user || !checked || pending.length === 0 || !tour) return null;

  const slide = tour.slides[step];
  const isLast = step === tour.slides.length - 1;
  const isFirst = step === 0;
  const Icon = slide.icon;

  const finishThisTour = async (goToCta = false) => {
    setSaving(true);
    try {
      await api.post("/onboarding/complete", { tour: tour.role });
    } catch { /* no-op — safe to retry */ }
    setSaving(false);

    if (tourIdx < pending.length - 1) {
      // Chained tours — a role=both user gets buyer, then seller
      setTourIdx(tourIdx + 1);
      setStep(0);
    } else {
      setPending([]);
      if (goToCta && tour.ctaRoute) nav(tour.ctaRoute);
    }
  };

  const skip = async () => {
    // Skipping still marks the tour done — user asked to dismiss it, they've
    // seen the modal and can replay from the menu if they want it back.
    await finishThisTour(false);
    toast.success("Tour dismissed — you can replay it any time.");
  };

  return (
    <div
      className="fixed inset-0 z-[9998] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="onboarding-tour"
      onClick={(e) => e.target === e.currentTarget && skip()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl overflow-hidden grid md:grid-cols-2">
        {/* --- Visual half --- */}
        <div
          className="relative p-8 flex flex-col justify-between text-white min-h-[420px] overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${slide.accent} 0%, ${slide.accent}dd 45%, #0F172A 130%)` }}
          data-testid="tour-visual"
        >
          {/* Ambient orbit */}
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-16 -left-8 w-56 h-56 rounded-full bg-white/5 blur-3xl" />

          <div className="relative flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            <span className="text-xs font-mono uppercase tracking-widest">Guided tour · {tour.role.replace("_", " ")}</span>
          </div>

          <div className="relative">
            <div className="rounded-2xl bg-white/15 backdrop-blur h-24 w-24 mb-6 flex items-center justify-center border border-white/30 shadow-lg">
              <Icon className="h-12 w-12" strokeWidth={1.4} />
            </div>
            <div className="font-mono text-[11px] uppercase tracking-widest opacity-70 mb-1">Step {step + 1} of {tour.slides.length}</div>
            <h3 className="font-display font-extrabold text-2xl sm:text-3xl leading-tight">{slide.title}</h3>
          </div>

          {/* Step dots */}
          <div className="relative flex gap-1.5">
            {tour.slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                data-testid={`tour-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${i === step ? "bg-white w-8" : "bg-white/40 w-4 hover:bg-white/60"}`}
                aria-label={`Go to step ${i + 1}`}
              />
            ))}
          </div>
        </div>

        {/* --- Copy half --- */}
        <div className="p-6 sm:p-8 flex flex-col">
          <div className="flex items-start justify-between gap-4 mb-1">
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{tour.subtitle}</p>
              <h2 className="font-display font-bold text-xl sm:text-2xl mt-1">{tour.title}</h2>
            </div>
            <button
              onClick={skip}
              data-testid="tour-skip"
              className="text-muted-foreground hover:text-foreground rounded-md p-1"
              aria-label="Skip tour"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <ul className="mt-6 space-y-3 flex-1" data-testid={`tour-slide-${step}`}>
            {slide.bullets.map((b, i) => (
              <li key={i} className="flex gap-3 text-sm leading-relaxed">
                <span className="mt-0.5 h-5 w-5 rounded-full text-white text-xs font-mono flex items-center justify-center shrink-0" style={{ background: slide.accent }}>
                  {i + 1}
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ul>

          {/* --- Nav row --- */}
          <div className="mt-8 flex items-center justify-between gap-3">
            <button
              onClick={() => setStep(step - 1)}
              disabled={isFirst}
              data-testid="tour-prev"
              className="text-sm text-muted-foreground disabled:opacity-40 disabled:pointer-events-none inline-flex items-center gap-1 hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>

            {!isLast ? (
              <button
                onClick={() => setStep(step + 1)}
                data-testid="tour-next"
                className="bg-primary text-primary-foreground rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 inline-flex items-center gap-2 shadow-sm"
              >
                Next <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={() => finishThisTour(true)}
                disabled={saving}
                data-testid="tour-finish"
                className="bg-primary text-primary-foreground rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 inline-flex items-center gap-2 shadow-sm disabled:opacity-60"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {tour.cta} <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
